// Periodisation frameworks for programme generation (#54).
//
// Each framework is an evidence-based programme structure the user picks when
// creating a programme. The selection is stored in `programmes.progression_style`
// and drives two things:
//   1. Day labels — the week planner (config/programmePlanner.js) stamps each
//      resistance day with its split day and framework day type. The server
//      stores the plan as sent; existing slots keep the label and day kind
//      they were built with, and display and generation read those back.
//   2. LLM constraints — `llmConstraints` is injected into the generation
//      prompt so every session respects the framework instead of drifting into
//      identical repeats.
//
// The framework is normally AUTO-SELECTED from the user's goal, schedule and
// experience (selectFramework below); advanced users can override it in the
// design form. Legacy "undulating" remains render-only for old programmes.

// The progression method every non-novice framework shares. The 2026 ACSM
// position stand found complex periodisation did not consistently beat plain
// progressive overload, so the frameworks are structure on top of this rule,
// not a substitute for it (docs/training_science.md §2, L3). Novice Linear
// keeps its own per-session progression: beginners can add load every time.
const DOUBLE_PROGRESSION =
  "Progression method: DOUBLE PROGRESSION. Keep the load fixed and add reps within the prescribed range week to week; once every working set reaches the top of the range, add the smallest load step (~2.5 kg upper body, ~5 kg lower body) and return to the bottom of the range.";

export const PERIODISATION_FRAMEWORKS = {
  block: {
    value: "block",
    label: "Block Periodisation",
    tagline: "One adaptation at a time",
    description:
      "The whole programme drives a single adaptation — strength, hypertrophy or endurance — chosen from your primary focus. Avoids the interference effect of chasing competing goals at once.",
    evidence: "Established standard in athletic programming",
    llmConstraints: [
      "Framework: BLOCK PERIODISATION. The entire block trains ONE primary adaptation, taken from the programme's focus. Do not mix competing adaptations in the main work (interference effect); accessories stay complementary.",
      "If the focus is power: explosive intent on every rep — jumps, throws, olympic-lift variations or fast concentric compounds at 30-70% 1RM, 3-5 reps per set, full recovery between sets, end each set as soon as speed drops and never near failure. Do NOT apply the heavy 3-5 strength protocol to a power focus.",
      'If the focus is strength: apply the 3-5 protocol — 3-5 compound exercises, 3-5 reps, 3-5 sets, 3-5 minutes rest, loads ≥85% 1RM on top sets.',
      "If the focus is hypertrophy: 3-4 exercises per muscle group in the 8-15 rep range, 3-4 sets, 90-120 s rest, sets taken 0-3 reps from failure.",
      "If the focus is endurance/conditioning: sustained aerobic work and intervals; keep resistance work light and supportive.",
      "Progress week-over-week within the block: small load or volume increments on the SAME core movement patterns — repeat the pattern, progress the dose. Do not reinvent the session each week.",
      DOUBLE_PROGRESSION,
    ].join(" "),
  },
  alternating: {
    value: "alternating",
    label: "Alternating Protocol",
    tagline: "Strength + hypertrophy, balanced",
    description:
      "Alternates Heavy sessions (4-8 reps, long rests) with Moderate sessions (8-15 reps, short rests) so you build strength and muscle in the same week. Dr. Andy Galpin's foundational general-preparedness protocol.",
    evidence: "High consensus — Galpin's foundational protocol",
    llmConstraints: [
      "Framework: ALTERNATING PROTOCOL (Galpin). Sessions alternate between two fixed schedules; this slot's schedule is stamped in its label.",
      'Schedule A "Heavy": compound-led, 4-8 reps, 3-4 sets, 2-4 minutes rest, high load (RPE 7-9).',
      'Schedule B "Moderate": 8-15 reps, 2-3 sets, ~90 s rest, moderate load focused on quality volume (RPE 6-8).',
      "Never blend the two schemes inside one session. Progress each schedule independently week-over-week (small load or rep increments on repeated movements).",
      DOUBLE_PROGRESSION,
    ].join(" "),
  },
  dup: {
    value: "dup",
    label: "Daily Undulating (DUP)",
    tagline: "Varied stimulus, same week",
    description:
      "Volume and intensity change session-to-session inside each week — a strength day, a hypertrophy day, a power day — reducing CNS fatigue and plateaus while developing several qualities at once.",
    evidence: "Well studied — similar results to simple progression, with more variety",
    llmConstraints: [
      "Framework: DAILY UNDULATING PERIODISATION. Each session in the week has a distinct day type, stamped in its label.",
      "Strength day: heavy compounds, 3-6 reps, 3-5 sets, 2-3 minutes rest.",
      "Hypertrophy day: 8-15 reps, 3-4 sets, 90-120 s rest, sets 0-3 reps from failure.",
      "Power day: explosive intent — jumps, throws, olympic-lift variations or fast concentric compound work at 30-70% 1RM, 3-5 reps per set, full recovery between sets, nothing to failure.",
      "Keep day types clearly distinct (do not let a hypertrophy day drift heavy), and progress each day type against ITS OWN previous week, not against the other day types.",
      DOUBLE_PROGRESSION,
    ].join(" "),
  },
  linear: {
    value: "linear",
    label: "Novice Linear Progression",
    tagline: "Master the basics, progress every session",
    description:
      "Repeats the same core compound lifts and adds a small amount of load every session — the fastest proven progression for newer lifters. Consistency beats variety until linear gains stall.",
    evidence: "Uncontroversial standard for novice lifters",
    llmConstraints: [
      "Framework: NOVICE LINEAR PROGRESSION. Build the session around 3-5 core compound movement patterns (squat, hinge, push, pull), 3 sets of 5-8 reps, 2-3 minutes rest.",
      "Repeat the SAME core lifts in the same slot every week and add a small increment relative to the previous completed week (~2.5 kg upper body / ~5 kg lower body, or +1 rep). Progress comes from load, not novelty — keep exercise variation minimal.",
      "At most 1-2 simple accessories after the core work. Technique quality gates every increment; no sets to failure.",
    ].join(" "),
  },
};

// Short badge names (incl. legacy values so old programmes keep rendering).
export const PROGRESSION_STYLE_LABELS = {
  block: "Block",
  alternating: "Alternating A/B",
  dup: "DUP",
  linear: "Novice Linear",
  undulating: "Undulating",
};

export const getFramework = (value) =>
  PERIODISATION_FRAMEWORKS[value] || null;

// Auto-select the framework from what the user actually commits to: goal,
// schedule and experience. Most users shouldn't have to choose between Block
// and DUP — advanced users can still override in the design form.
//   beginner                      → linear (progress every session; periodisation
//                                   solves a problem novices don't have yet)
//   strength / hypertrophy goal   → block (one adaptation on every lifting day)
//   endurance goal                → alternating: the cardio is planned as its
//                                   own Zone 2 and interval days, so the
//                                   lifting days stay general strength (#92)
//   general goal + ≥3 lifting days → dup (enough days to rotate 3 day types)
//   everything else               → alternating (Galpin GPP default; also the
//                                   1-2 lifting days answer, where DUP can't rotate)
//
// `sessionsPerWeek` is the number of LIFTING days a week (the planner's
// resistance count), not every training day.
export const selectFramework = ({ focus, sessionsPerWeek, fitnessLevel }) => {
  const goal = (focus || "").toLowerCase();
  const perWeek = Number(sessionsPerWeek) || 2;

  if ((fitnessLevel || "").toLowerCase() === "beginner") return "linear";

  const isStrength = /\b(stronger|strength|power)\b/.test(goal);
  const isEndurance =
    /endurance|vo2|zone 2|cardio|metcon|metabolic|work capacity/.test(goal);
  const isHypertrophy = /muscle|hypertrophy/.test(goal);
  const singleAdaptation =
    [isStrength, isEndurance, isHypertrophy].filter(Boolean).length === 1;
  if (singleAdaptation && !isEndurance) return "block";

  if (perWeek >= 3 && !isEndurance) return "dup";
  return "alternating";
};

// Deload placement: programmes of
// 6+ weeks are cut into the fewest blocks of at most 7 weeks, as even as
// possible, and each block ends on a deload, so no more than 6 hard weeks run
// back to back (8 weeks → 4, 8; 12 → 6, 12; 16 → 6, 11, 16).
const MAX_BLOCK_WEEKS = 7;

export const getDeloadWeeks = (durationWeeks) => {
  const weeks = Number(durationWeeks) || 0;
  if (weeks < 6) return [];
  const blocks = Math.ceil(weeks / MAX_BLOCK_WEEKS);
  const deloads = [];
  let end = 0;
  for (let b = 0; b < blocks; b++) {
    // Longer blocks first when the weeks don't divide evenly.
    end += Math.ceil((weeks - end) / (blocks - b));
    deloads.push(end);
  }
  return deloads;
};

export const isDeloadWeek = (weekNumber, durationWeeks) =>
  getDeloadWeeks(durationWeeks).includes(Number(weekNumber));

/** "6", "6 and 12", "6, 11 and 16". */
export const formatWeekList = (weeks) =>
  weeks.length > 1
    ? `${weeks.slice(0, -1).join(", ")} and ${weeks[weeks.length - 1]}`
    : String(weeks[0] ?? "");

export const DELOAD_CONSTRAINT =
  "THIS IS A DELOAD SESSION: cut working-set volume to roughly half of a normal week, keep loads light (~50-60% of usual), take nothing near failure, and bias movement quality, technique and mobility. The goal is recovery while staying in motion.";

// Split days in rotation order.
const SPLIT_CYCLES = {
  full_body: ["Full Body"],
  upper_lower: ["Upper Body", "Lower Body"],
  ppl: ["Push", "Pull", "Legs"],
  body_part: ["Chest & Triceps", "Back & Biceps", "Shoulders & Abs", "Legs"],
};

const FRAMEWORK_DAY_TYPES = {
  alternating: ["Heavy A", "Moderate B"],
  dup: ["Strength", "Hypertrophy", "Power"],
};

const splitCycle = (split) => SPLIT_CYCLES[split] || SPLIT_CYCLES.full_body;

// Where a slot sits in the programme's run of sessions. The split carries on
// across weeks rather than restarting each Monday, so push/pull/legs at 2 a
// week still reaches Legs.
const slotPosition = (split, perWeek, weekNumber, dayIndex) => {
  const cycle = splitCycle(split);
  const n = (Number(weekNumber) - 1) * (Number(perWeek) || 1) + Number(dayIndex);
  return { cycle, n, splitIndex: n % cycle.length, round: Math.floor(n / cycle.length) };
};

const splitLabel = (split, perWeek, weekNumber, dayIndex) => {
  const { cycle, splitIndex } = slotPosition(split, perWeek, weekNumber, dayIndex);
  return cycle[splitIndex];
};

// Framework day archetype for a slot. Each split day steps through the day
// types on its own, offset by its place in the split so a week still mixes
// them: upper/lower Alternating at 4 a week is Upper Heavy, Lower Moderate,
// Upper Moderate, Lower Heavy. Rotating on the slot alone lines up with the
// split's own cycle, and Lower never went heavy (#92).
export const dayTypeForSlot = (framework, perWeek, weekNumber, dayIndex, split) => {
  const types = FRAMEWORK_DAY_TYPES[framework];
  if (!types) return null; // block / linear / legacy: the split label carries the identity
  const { splitIndex, round } = slotPosition(split, perWeek, weekNumber, dayIndex);
  return types[(round + splitIndex) % types.length];
};

/**
 * Split day and framework day type for the programme's n-th lifting day
 * (0-based, counting lifting days only). The planner uses this so Zone 2 and
 * interval days don't move the split or the heavy/moderate rotation along.
 */
export const resistanceDayAt = (framework, split, n) => ({
  base: splitLabel(split, 1, 1, n),
  dayType: dayTypeForSlot(framework, 1, 1, n, split),
});

// ─── Stored slot labels ─────────────────────────────────────────────────────
// The server stamps each slot's label when the plan is built, and the rules
// above have changed over time. Reading the stored label keeps an older
// programme consistent with its own week plan instead of recomputing it under
// newer rules.
const KNOWN_DAY_TYPES = new Set(Object.values(FRAMEWORK_DAY_TYPES).flat());
const KNOWN_SPLIT_DAYS = new Set(Object.values(SPLIT_CYCLES).flat());

// Cardio day labels the planner writes, and the day kind each one means.
export const CARDIO_LABELS = {
  "Zone 2": "zone2",
  "Long Zone 2": "zone2",
  "Zone 2 + Sprints": "zone2_sprints",
  "Long Zone 2 + Sprints": "zone2_sprints",
  VO2max: "vo2max",
  "Sprint Intervals": "intervals",
  Intervals: "intervals",
};
export const FINISHER_SUFFIX = " + Intervals";

// Day kinds (programme_sessions.day_kind). Null on slots built before #92,
// which were all lifting days. resistance_intervals is a moderate lifting day
// with a short all-out finisher; zone2_sprints is a Zone 2 day ending in a
// sprint block (config/programmePlanner.js decides which, and when).
export const DAY_KINDS = [
  "resistance",
  "zone2",
  "zone2_sprints",
  "vo2max",
  "intervals",
  "resistance_intervals",
];
const CARDIO_KINDS = new Set(["zone2", "zone2_sprints", "vo2max", "intervals"]);
export const isCardioKind = (kind) => CARDIO_KINDS.has(kind);

export const parseScheduledLabel = (label) => {
  if (typeof label !== "string" || !label.trim()) return null;
  const parts = label.split(" · ").map((p) => p.trim());
  const isDeload = parts[0] === "Deload";
  if (isDeload) parts.shift();
  if (CARDIO_LABELS[parts[0]]) {
    return { isDeload, base: parts[0], dayType: null, kind: CARDIO_LABELS[parts[0]] };
  }
  const finisher = parts[parts.length - 1]?.endsWith(FINISHER_SUFFIX);
  if (finisher) {
    parts[parts.length - 1] = parts[parts.length - 1].slice(0, -FINISHER_SUFFIX.length);
  }
  const base = KNOWN_SPLIT_DAYS.has(parts[0]) ? parts[0] : null;
  const dayType = KNOWN_DAY_TYPES.has(parts[1]) ? parts[1] : null;
  if (!base) return null;
  return { isDeload, base, dayType, kind: finisher ? "resistance_intervals" : "resistance" };
};

// The slot's split day, day type, kind, deload flag and minutes: from what was
// stored when it was built, otherwise from the current rules.
export const slotIdentity = (programme, session) => {
  const minutes =
    Number(session?.target_minutes) > 0
      ? Number(session.target_minutes)
      : Number(programme.session_minutes) > 0
        ? Number(programme.session_minutes)
        : null;
  // An imported programme is as written: its labels are the coach's own,
  // and it has no planned deloads.
  if (programme?.source === "imported") {
    return {
      isDeload: false,
      base: session?.scheduled_label || "Session",
      dayType: null,
      kind: session?.day_kind || "resistance",
      minutes,
    };
  }
  const parsed = parseScheduledLabel(session?.scheduled_label);
  if (parsed) {
    return { ...parsed, kind: session.day_kind || parsed.kind, minutes };
  }
  const perWeek = programme.days_per_week;
  return {
    isDeload: isDeloadWeek(session.week_number, programme.duration_weeks),
    base: splitLabel(programme.split, perWeek, session.week_number, session.day_index),
    dayType: dayTypeForSlot(
      programme.progression_style,
      perWeek,
      session.week_number,
      session.day_index,
      programme.split,
    ),
    kind: session?.day_kind || "resistance",
    minutes,
  };
};

/** Day kind and minutes for a slot, as built. */
export const slotPlan = (programme, session) => {
  const { kind, minutes, isDeload } = slotIdentity(programme, session);
  return { kind, minutes, isDeload };
};

/**
 * Deload weeks for a programme as built: weeks the user moved them to (#94)
 * when stored, else weeks whose stored labels say Deload, falling back to the
 * current rule when there are no slots to read. (A deload moved onto a week
 * already done keeps that week's labels, so the stored list wins.)
 */
export const programmeDeloadWeeks = (programme) => {
  if (programme?.source === "imported") return [];
  if (Array.isArray(programme?.deload_weeks) && programme.deload_weeks.length) {
    return programme.deload_weeks.map(Number).sort((a, b) => a - b);
  }
  const weeks = programme?.weeks || [];
  const labelled = weeks.filter((w) =>
    (w.sessions || []).some((s) => parseScheduledLabel(s.scheduled_label)),
  );
  if (!labelled.length) return getDeloadWeeks(programme?.duration_weeks);
  return labelled
    .filter((w) =>
      (w.sessions || []).some((s) => parseScheduledLabel(s.scheduled_label)?.isDeload),
    )
    .map((w) => w.week_number);
};

/** Whether a slot belongs to a deload week, by its stored label when present. */
export const isDeloadSlot = (programme, session) =>
  slotIdentity(programme, session).isDeload;

// Full scheduled label for a slot — used for the design-form preview; the
// server writes the same shape into programme_sessions.scheduled_label.
export const buildScheduledLabel = ({
  framework,
  split,
  perWeek,
  weekNumber,
  dayIndex,
  durationWeeks,
  deloadWeeks,
}) => {
  const base = splitLabel(split, perWeek, weekNumber, dayIndex);
  const dayType = dayTypeForSlot(framework, perWeek, weekNumber, dayIndex, split);
  let label = dayType ? `${base} · ${dayType}` : base;
  const deload = Array.isArray(deloadWeeks)
    ? deloadWeeks.map(Number).includes(Number(weekNumber))
    : isDeloadWeek(weekNumber, durationWeeks);
  if (deload) label = `Deload · ${label}`;
  return label;
};

// ─── Slot recommendation (display) ──────────────────────────────────────────
// The concrete set/rep/rest targets a framework prescribes for a given day
// type — shown in the session panel so the user sees what the programme
// recommends while editing (#59 follow-up). Distilled from each framework's
// llmConstraints into displayable ranges.
const DAY_TYPE_RECOMMENDATION = {
  "Heavy A": { sets: "3\u20134", reps: "4\u20138", rest: "2\u20134 min", intensity: "Heavy \u00b7 RPE 7\u20139" },
  "Moderate B": { sets: "2\u20133", reps: "8\u201315", rest: "~90 s", intensity: "Moderate \u00b7 RPE 6\u20138" },
  Strength: { sets: "3\u20135", reps: "3\u20136", rest: "2\u20133 min", intensity: "Heavy" },
  Hypertrophy: { sets: "3\u20134", reps: "8\u201315", rest: "60\u201390 s", intensity: "1\u20133 reps in reserve" },
  Power: { sets: "3\u20135", reps: "3\u20135", rest: "Full recovery", intensity: "Explosive \u00b7 never to failure" },
};

// Power is checked before strength: "Power Development" wants fast, light
// work at 30-70% 1RM, the opposite of the 3-5 protocol's heavy top sets.
const isPowerFocus = (g) => /\bpower\b/.test(g);

const blockFocusRecommendation = (focus) => {
  const g = (focus || "").toLowerCase();
  if (isPowerFocus(g)) return DAY_TYPE_RECOMMENDATION.Power;
  if (/\b(stronger|strength)\b/.test(g))
    return { sets: "3\u20135", reps: "3\u20135", rest: "3\u20135 min", intensity: "Heavy \u00b7 \u226585% 1RM (3-5 protocol)" };
  if (/muscle|hypertrophy/.test(g))
    return { sets: "3\u20134", reps: "8\u201315", rest: "60\u201390 s", intensity: "1\u20133 reps in reserve" };
  if (/endur|vo2|zone 2|cardio|metcon|metabolic|work capacity/.test(g))
    return { note: "Sustained aerobic work + intervals; keep any resistance work light and supportive." };
  return null;
};

/**
 * Structured, displayable recommendation for a programme slot: the framework,
 * day type, target sets/reps/rest/intensity, session length, and deload flag.
 * Returns null when there's nothing programme-specific to say.
 */
const ZONE2_NOTE =
  "Easy, continuous aerobic work at a pace you could hold a conversation at, breathing through your nose. No lifting today.";
const CARDIO_RECOMMENDATION = {
  zone2: { note: ZONE2_NOTE },
  zone2_sprints: {
    note: "Mostly easy Zone 2, then a short sprint block at the end: 4\u20136 all-out efforts of 20\u201330 seconds with easy recovery between.",
  },
  vo2max: {
    note: "Hard aerobic intervals: 3\u20134 rounds of 3\u20134 minutes at 90\u201395% of max heart rate, with 2\u20133 minutes easy between. Warm up first.",
  },
  intervals: {
    note: "All-out sprint intervals: 20\u201390 second efforts adding up to about 5\u20136 minutes, with full recovery between.",
  },
};

export const getSlotRecommendation = (programme, session) => {
  if (!programme || !session) return null;
  const framework = programme.progression_style;
  const { dayType, isDeload, kind, minutes } = slotIdentity(programme, session);
  let rec = null;
  if (CARDIO_RECOMMENDATION[kind]) rec = CARDIO_RECOMMENDATION[kind];
  else if (dayType) rec = DAY_TYPE_RECOMMENDATION[dayType];
  else if (framework === "linear")
    rec = { sets: "3", reps: "5\u20138", rest: "2\u20133 min", intensity: "Add a little load each week" };
  else if (framework === "block") rec = blockFocusRecommendation(programme.focus);

  return {
    framework: getFramework(framework)?.label || PROGRESSION_STYLE_LABELS[framework] || framework,
    dayType,
    label: session.scheduled_label,
    durationMin: minutes,
    kind,
    finisher: kind === "resistance_intervals",
    isDeload,
    ...(rec || {}),
  };
};

// ─── Structured slot → generation-dialog defaults ───────────────────────────
// The slot's identity used to reach the LLM only as prompt prose while the
// dialog's knobs stayed on profile defaults (misleading, and user edits fought
// the framework). These map the slot to the dialog's own structured fields.

const DAY_TYPE_STYLES = {
  "Heavy A": ["strength"],
  "Moderate B": ["hypertrophy"],
  Strength: ["strength"],
  Hypertrophy: ["hypertrophy"],
  Power: ["power"],
};

// Muscle-focus lists per split label. Values mirror TARGET_AREA_PRESETS in
// config/targets.js; "Full Body" is an explicit empty list so a programme slot
// clears any saved profile targets rather than skewing towards them.
const SPLIT_TARGET_AREAS = {
  "Full Body": [],
  "Upper Body": [
    "Pectoralis Major",
    "Lats",
    "Traps",
    "Anterior Deltoid",
    "Biceps",
    "Triceps",
  ],
  "Lower Body": ["Glutes", "Quads", "Hamstrings", "Calves", "Hip Flexors"],
  Push: ["Pectoralis Major", "Anterior Deltoid", "Triceps"],
  Pull: ["Lats", "Traps", "Rhomboids", "Biceps", "Posterior Deltoid"],
  Legs: ["Glutes", "Quads", "Hamstrings", "Calves", "Hip Flexors"],
  "Chest & Triceps": ["Pectoralis Major", "Pectoralis Minor", "Triceps"],
  "Back & Biceps": ["Lats", "Traps", "Rhomboids", "Biceps"],
  "Shoulders & Abs": [
    "Anterior Deltoid",
    "Lateral Deltoid",
    "Posterior Deltoid",
    "Abs",
    "Obliques",
  ],
};

const CARDIO_STYLES = {
  zone2: ["cardio"],
  zone2_sprints: ["cardio", "hiit"],
  vo2max: ["cardio"],
  intervals: ["hiit"],
};

const stylesForBlockFocus = (focus) => {
  const goal = (focus || "").toLowerCase();
  if (isPowerFocus(goal)) return ["power"];
  if (/\b(stronger|strength)\b/.test(goal)) return ["strength"];
  if (/muscle|hypertrophy/.test(goal)) return ["hypertrophy"];
  if (/endurance|vo2|zone 2|cardio|metcon|metabolic|work capacity/.test(goal))
    return ["conditioning"];
  return null; // ambiguous focus: leave the user's profile styles alone
};

/**
 * Structured generation defaults for a programme slot: session duration from
 * the day's planned minutes, training styles from its kind and framework day
 * type, muscle
 * focus from the split, goal from the programme focus. Merged over the
 * profile defaults when the Generate dialog opens from a slot; everything
 * stays user-editable. Returns undefined when there is nothing to prefill.
 */
export const buildSlotGenerationDefaults = (programme, targetSession) => {
  if (!programme || !targetSession) return undefined;

  const framework = programme.progression_style;
  const { base, dayType, kind, minutes } = slotIdentity(programme, targetSession);

  const defaults = {};
  if (minutes) defaults.sessionDuration = minutes;
  if (programme.focus) defaults.primaryGoal = programme.focus;

  // Cardio days. VO2max work is aerobic training done hard, so it is built
  // as cardio; all-out sprints are the hard-interval (anaerobic) work.
  if (isCardioKind(kind)) {
    defaults.workoutTypes = [...CARDIO_STYLES[kind]];
    defaults.targetAreas = [];
    return defaults;
  }

  const styles = dayType
    ? DAY_TYPE_STYLES[dayType]
    : framework === "linear"
      ? ["strength"]
      : framework === "block"
        ? stylesForBlockFocus(programme.focus)
        : null;
  if (styles) defaults.workoutTypes = [...styles];
  if (kind === "resistance_intervals") {
    defaults.workoutTypes = [...(defaults.workoutTypes || []), "hiit"];
  }

  const targets = SPLIT_TARGET_AREAS[base];
  if (targets) defaults.targetAreas = [...targets];

  return Object.keys(defaults).length ? defaults : undefined;
};
